/**
 * NOS-220: Shared blog-post metadata helpers used by the public post page,
 * the dashboard preview, and the builder canvas so all three surfaces agree
 * on reading time, date formatting, and hero-block handling.
 */
import type { PageSection } from "@/templates/types"

/**
 * Estimate reading time in minutes from all blocks' text content (~200 wpm).
 * Imported paragraph blocks carry a `text` plain-text mirror of their TipTap
 * doc, so flat string collection covers both imported and hand-authored
 * blocks. Source "N minutes to read" strings are stripped from bodies as
 * boilerplate (NOS-204); this computed value replaces them.
 */
export function calcReadingTime(blocks: PageSection[]): number {
  const text = blocks
    .map((b) => {
      const c = (b.content as Record<string, unknown>) ?? {}
      return Object.values(c)
        .filter((v) => typeof v === "string")
        .join(" ")
    })
    .join(" ")
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

/** Format an ISO date as e.g. "July 17, 2026". Returns null when absent. */
export function formatPostDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

/**
 * Split a post's blocks into an optional leading hero block and the body.
 * Builder-created posts start with a hero section whose presentation is
 * replaced by the shared BlogPostHeader; imported posts have no hero block.
 * Blocks are returned sorted by order_index.
 */
export function splitLeadingHero(blocks: PageSection[]): {
  heroBlock: PageSection | null
  bodyBlocks: PageSection[]
} {
  const sorted = [...blocks].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
  if (sorted.length > 0 && sorted[0].section_type === "hero") {
    return { heroBlock: sorted[0], bodyBlocks: sorted.slice(1) }
  }
  return { heroBlock: null, bodyBlocks: sorted }
}
